export const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api/v1';

// Must match the backend's csrfCookieName (backend/internal/httpapi/middleware.go).
const CSRF_COOKIE = 'k8sum_csrf';

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

// A minimal structural type so we don't couple the client to zod directly.
type ResponseValidator = { safeParse: (d: unknown) => { success: boolean; error?: { message?: string } } };

type FetchOptions = RequestInit & {
  params?: Record<string, string | number | boolean | undefined | null>;
  skipRefreshOn401?: boolean;
  // When set, the (camelCased) response is validated against this schema. On a
  // mismatch we fail loudly in dev/test/e2e (throw) and log-and-continue in
  // production — so backend/frontend contract drift can't hide behind mocks.
  schema?: ResponseValidator;
};

function buildUrl(path: string, params?: FetchOptions['params']): string {
  const url = path.startsWith('http') ? new URL(path) : new URL(API_BASE + path, window.location.origin);
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined || v === null || v === '') continue;
      url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

// --- Case normalization -------------------------------------------------
// The frontend speaks camelCase; the Go backend speaks snake_case. Convert at
// the wire boundary so neither side has to know about the other's convention:
// request bodies go out snake_case, responses come back camelCase. Keys that
// are already in the target case pass through unchanged (the transform is a
// no-op on them), so it is safe to apply blanket.
const camelToSnake = (k: string) => k.replace(/[A-Z]/g, (m) => '_' + m.toLowerCase());
const snakeToCamel = (k: string) => k.replace(/_([a-z0-9])/g, (_, c: string) => c.toUpperCase());

function mapKeysDeep(value: unknown, fn: (k: string) => string): unknown {
  if (Array.isArray(value)) return value.map((v) => mapKeysDeep(v, fn));
  // Only remap plain objects — leave Date, File, etc. untouched.
  if (value && typeof value === 'object' && (value as object).constructor === Object) {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => [fn(k), mapKeysDeep(v, fn)]),
    );
  }
  return value;
}

export const toSnakeKeys = (v: unknown) => mapKeysDeep(v, camelToSnake);
export const toCamelKeys = (v: unknown) => mapKeysDeep(v, snakeToCamel);

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()\[\]\\\/\+^])/g, '\\$1') + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

let refreshing: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  if (refreshing) return refreshing;
  refreshing = (async () => {
    try {
      const res = await fetch(buildUrl('/auth/refresh'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-Requested-With': 'fetch' },
      });
      return res.ok;
    } catch {
      return false;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

export async function apiFetch<T = unknown>(path: string, opts: FetchOptions = {}): Promise<T> {
  const { params, skipRefreshOn401, schema, headers, ...init } = opts;
  const url = buildUrl(path, params);

  const finalHeaders = new Headers(headers || {});
  if (!finalHeaders.has('Accept')) finalHeaders.set('Accept', 'application/json');
  if (init.body && typeof init.body === 'string' && !finalHeaders.has('Content-Type')) {
    finalHeaders.set('Content-Type', 'application/json');
  }
  finalHeaders.set('X-Requested-With', 'fetch');
  const csrf = readCookie(CSRF_COOKIE);
  if (csrf && !finalHeaders.has('X-CSRF-Token')) finalHeaders.set('X-CSRF-Token', csrf);

  const doRequest = () =>
    fetch(url, {
      ...init,
      credentials: 'include',
      headers: finalHeaders,
    });

  let res = await doRequest();

  if (res.status === 401 && !skipRefreshOn401 && !path.startsWith('/auth/')) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      res = await doRequest();
    }
  }

  if (res.status === 204) {
    return undefined as T;
  }

  const contentType = res.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json') || contentType.includes('application/problem+json');
  const body = isJson
    ? toCamelKeys(await res.json().catch(() => undefined))
    : await res.text().catch(() => undefined);

  if (!res.ok) {
    let message = res.statusText || `HTTP ${res.status}`;
    if (body && typeof body === 'object') {
      const b = body as { title?: string; detail?: string; message?: string };
      message = b.detail || b.title || b.message || message;
    } else if (typeof body === 'string' && body) {
      message = body;
    }
    throw new ApiError(res.status, message, body);
  }

  if (schema && body && typeof body === 'object') {
    const r = schema.safeParse(body);
    if (!r.success) {
      const detail = r.error?.message ?? '';
      const msg = `API response for ${path} did not match its schema — backend/frontend contract drift.`;
      if (import.meta.env.MODE !== 'production') {
        throw new Error(`${msg}\n${detail}`);
      }
      // eslint-disable-next-line no-console
      console.error(msg, r.error);
    }
  }

  return body as T;
}

export const api = {
  get: <T>(path: string, opts: FetchOptions = {}) => apiFetch<T>(path, { ...opts, method: 'GET' }),
  post: <T>(path: string, data?: unknown, opts: FetchOptions = {}) =>
    apiFetch<T>(path, { ...opts, method: 'POST', body: data === undefined ? undefined : JSON.stringify(toSnakeKeys(data)) }),
  put: <T>(path: string, data?: unknown, opts: FetchOptions = {}) =>
    apiFetch<T>(path, { ...opts, method: 'PUT', body: data === undefined ? undefined : JSON.stringify(toSnakeKeys(data)) }),
  patch: <T>(path: string, data?: unknown, opts: FetchOptions = {}) =>
    apiFetch<T>(path, { ...opts, method: 'PATCH', body: data === undefined ? undefined : JSON.stringify(toSnakeKeys(data)) }),
  del: <T>(path: string, opts: FetchOptions = {}) => apiFetch<T>(path, { ...opts, method: 'DELETE' }),
};
